import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  PersonOutline as PersonIcon,
  PhoneOutlined as PhoneIcon,
  LoginOutlined as LoginIcon,
} from '@mui/icons-material';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import candidateTheme from '../../theme/candidateTheme';
import api from '../../api';
import { showSuccess, showError } from '../../utils/sweetAlertConfig';

/**
 * Professional and clean candidate login component
 * Uses Material-UI for consistent, modern design
 */
const CandidateLogin = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Form state management
  const [formData, setFormData] = useState({
    candidate_id: '',
    mobile: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /**
   * Real-time field validation
   * Provides immediate feedback to users
   */
  const validateField = (name, value) => {
    switch (name) {
      case 'candidate_id':
        if (!value.trim()) return 'Candidate ID is required';
        if (value.length < 3) return 'Candidate ID must be at least 3 characters';
        return '';
      case 'mobile':
        if (!value.trim()) return 'Mobile number is required';
        if (!/^[0-9]{10,11}$/.test(value.replace(/\s+/g, ''))) {
          return 'Please enter a valid mobile number (10-11 digits)';
        }
        return '';
      default:
        return '';
    }
  };

  /**
   * Handle input changes with real-time validation
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field and validate
    const fieldError = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  /**
   * Form submission handler
   * Validates all fields and makes API request
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/candidate/login', formData);
      
      if (response.status === 200) {
        const { candidate, token } = response.data;

        console.log('Login successful!', { candidate, token });

        // Store authentication data
        localStorage.setItem('candidate_token', token);
        localStorage.setItem('candidate_data', JSON.stringify(candidate));

        console.log('Token stored:', localStorage.getItem('candidate_token'));

        showSuccess('Welcome!', 'Login successful. Redirecting...');

        // Check if birth_date exists (which means SSN was entered)
        // If birth_date is missing, go to SSN completion
        setTimeout(() => {
          if (!candidate.birth_date) {
            navigate('/candidate/complete-ssn');
          } else {
            navigate('/candidate/assessments');
          }
        }, 500);
      }
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Login failed. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showError('Login Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get validation state for input styling
   */
  const getFieldError = (fieldName) => {
    return errors[fieldName] || '';
  };

  return (
    <ThemeProvider theme={candidateTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(-45deg, #29204C, #1a1530, #29204C, #342854)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 15s ease infinite',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 3 },
          '@keyframes gradientShift': {
            '0%': {
              backgroundPosition: '0% 50%'
            },
            '50%': {
              backgroundPosition: '100% 50%'
            },
            '100%': {
              backgroundPosition: '0% 50%'
            }
          }
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={1}
            sx={{
              padding: { xs: 3, sm: 4, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              backgroundColor: 'background.paper',
            }}
          >
            {/* Header Section */}
            <Box sx={{ textAlign: 'center', mb: 4, width: '100%' }}>
              {/* Company Logo */}
              <Box sx={{ mb: 3 }}>
                <img 
                  src="/zonnetLogo.png" 
                  alt="ZonNet Logo" 
                  style={{
                    width: 'auto',
                    maxWidth: '200px',
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    // Fallback if logo not found
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <Typography 
                  variant="h4" 
                  sx={{ 
                    display: 'none', 
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 1
                  }}
                >
                  ZonNet
                </Typography>
              </Box>

              <Typography
                variant="h3"
                component="h1"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: 'primary.main',
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                }}
              >
                Candidate Portal
              </Typography>
              
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                Enter your credentials to access your profile and manage your application
              </Typography>
              
              <Divider sx={{ width: '60px', margin: '0 auto', height: '3px', backgroundColor: 'primary.main' }} />
            </Box>

            {/* Login Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: '100%', maxWidth: '400px' }}
            >
              {/* Candidate ID Field */}
              <TextField
                fullWidth
                name="candidate_id"
                label="Candidate ID"
                value={formData.candidate_id}
                onChange={handleInputChange}
                error={!!getFieldError('candidate_id')}
                helperText={getFieldError('candidate_id')}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
                placeholder="Enter your candidate ID"
              />

              {/* Mobile Number Field */}
              <TextField
                fullWidth
                name="mobile"
                label="Mobile Number"
                type="tel"
                value={formData.mobile}
                onChange={handleInputChange}
                error={!!getFieldError('mobile')}
                helperText={getFieldError('mobile')}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 4 }}
                placeholder="Enter your mobile number"
              />

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{
                  py: 1.5,
                  mb: 3,
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                {loading ? 'Signing In...' : 'Access My Profile'}
              </Button>

              {/* Help Text */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.disabled">
                  Having trouble accessing your account?
                </Typography>
                <br />
                <Typography variant="caption" color="text.disabled">
                  Contact the administrator for assistance.
                </Typography>
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center', width: '100%' }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="caption" color="text.disabled">
                © 2024 ZonNet Recruitment System. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CandidateLogin;